import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { Badge } from "@/components/ui/badge";
import { BatchClearanceStatusEnum } from "@brewbase/db/types";
import { DataTable, type DataTableColumnDef } from "@/components/ui/data-table";
import { formatDate } from "@/lib/date";

export const Route = createFileRoute("/harvests/batches/")({
  component: HarvestBatchesPage,
});

type Row =
  RouterOutputs["batches"]["brewBatches"]["listAllWithHarvestGroup"][number];

const prettyStatus = (s: string) =>
  s
    .toLowerCase()
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const clearanceVariant = (
  s: BatchClearanceStatusEnum,
): "default" | "secondary" | "destructive" | "outline" | "success" => {
  if (
    s === BatchClearanceStatusEnum.CLEARED ||
    s === BatchClearanceStatusEnum.CONDITIONALLY_CLEARED
  )
    return "success";
  if (
    s === BatchClearanceStatusEnum.TESTS_IN_PROGRESS ||
    s === BatchClearanceStatusEnum.PENDING_APPROVAL
  )
    return "secondary";
  if (s === BatchClearanceStatusEnum.REJECTED) return "destructive";
  return "outline";
};

function HarvestBatchesPage() {
  const { data: allBatches } =
    trpc.batches.brewBatches.listAllWithHarvestGroup.useQuery();

  const [clearanceFilter, setClearanceFilter] = useState<
    BatchClearanceStatusEnum[]
  >([]);

  const filtered = useMemo(() => {
    if (!allBatches) return [];
    if (!clearanceFilter.length) return allBatches;
    return allBatches.filter((b) =>
      clearanceFilter.includes(b.clearance_status as BatchClearanceStatusEnum),
    );
  }, [allBatches, clearanceFilter]);

  const columns = useMemo<DataTableColumnDef<Row, any>[]>(
    () => [
      {
        id: "harvest_group_name",
        accessorKey: "harvest_group_name",
        header: "Harvest Batches",
        enableGrouping: true,
        cell: ({ row }) => (
          <span>{(row.original as any).harvest_group_name}</span>
        ),
        size: 300,
        meta: {
          leafCell: ({ row }) => (
            <div className="pl-8 flex items-center gap-2">
              <Link
                to="/harvests/batches/$batch_id"
                params={{ batch_id: row.original.id }}
                className="hover:underline"
              >
                {row.original.name}
              </Link>
            </div>
          ),
        },
      },
      {
        id: "batch_type",
        accessorKey: "batch_type",
        header: "Type",
        enableSorting: true,
        cell: ({ row }) => {
          if (row.getIsGrouped()) return null;
          return <span>{row.original.batch_type ?? ""}</span>;
        },
      },
      {
        id: "clearance_status",
        accessorKey: "clearance_status",
        header: "Clearance",
        enableSorting: true,
        cell: ({ row }) => {
          if (row.getIsGrouped()) return null;
          return (
            <Badge
              variant={clearanceVariant(
                row.original.clearance_status as BatchClearanceStatusEnum,
              )}
              className="text-xs"
            >
              {prettyStatus(row.original.clearance_status)}
            </Badge>
          );
        },
      },
      {
        id: "responsible_operator_name",
        accessorKey: "responsible_operator_name",
        header: "Responsible",
        enableSorting: true,
        cell: ({ row }) => {
          if (row.getIsGrouped()) return null;
          return <span>{(row.original as any).responsible_operator_name ?? ""}</span>;
        },
      },
      {
        id: "produced_at",
        accessorKey: "produced_at",
        header: "Produced",
        enableSorting: true,
        cell: ({ row }) => {
          if (row.getIsGrouped()) return null;
          const val = (row.original as any).produced_at;
          if (!val) return null;
          return <span>{formatDate(val)}</span>;
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-3 mt-2 h-full min-h-0 grow container mx-auto">
      <DataTable
        columns={columns}
        data={filtered ?? []}
        defaultExpanded
        disableCollapse
        defaultGrouping={["harvest_group_name"]}
        hardcodedGrouping={true}
        enableGrouping={true}
        searchPlaceholder="Search batches…"
        enableColumnResizing={true}
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />
    </div>
  );
}
